// レイヤー一覧パネル。
//
// 役割: レイヤーの表示/非表示・ズーム・削除と、undo/redo（plan.md §6.3）を操作する。
//       各行の詳細ボタンでモーダル（LayerDetailModal）を開き、レイヤーID・フィーチャー数・
//       エクスポート・属性テーブルプレビューを確認できる。
// 関係: layers は layer-store の描画オブジェクト配列。各操作は App 経由で
//       layer-store + layer-service に伝わる。
import { useState } from 'react'

import LayerDetailModal from './LayerDetailModal'

function LayerRow({ layer, onToggle, onZoom, onRemove, onMention, onShowDetail }) {
  // コロプレス凡例の開閉（既定は開）。レイヤーごとに独立。
  const [legendOpen, setLegendOpen] = useState(true)

  return (
    <li className="layer-row">
      <div className="layer-row-main">
        <input
          type="checkbox"
          checked={layer.visible !== false}
          onChange={() => onToggle(layer.layerId)}
          title="表示/非表示"
        />
        <span
          className="swatch"
          style={{ background: `rgb(${(layer.color ?? [255, 140, 0]).join(',')})` }}
        />
        <span className="layer-name">
          <code>{layer.displayName ?? layer.layerId}</code>{' '}
          <small>
            ({layer.geomType ?? '—'}・{layer.featureCount})
          </small>
        </span>
        <button
          className="icon-btn"
          onClick={() => onMention(layer.layerId)}
          title="プロンプトにレイヤーIDを送る"
        >
          @
        </button>
        <button className="icon-btn" onClick={() => onShowDetail(layer)} title="レイヤー詳細">
          ⓘ
        </button>
        <button className="icon-btn" onClick={() => onZoom(layer.layerId)} title="この範囲にズーム">
          ⌖
        </button>
        <button
          className="icon-btn danger"
          onClick={() => onRemove(layer.layerId)}
          title="削除"
        >
          ×
        </button>
      </div>

      {layer.style?.type === 'choropleth' && layer.style.legend?.length > 0 && (
        <div className="layer-legend">
          <button
            className="legend-title"
            onClick={() => setLegendOpen((v) => !v)}
            aria-expanded={legendOpen}
            title="凡例を開閉"
          >
            <span className="legend-caret">{legendOpen ? '▾' : '▸'}</span>
            {layer.style.column}
          </button>
          {legendOpen &&
            layer.style.legend.map((e) => (
              <div className="legend-row" key={e.label}>
                <span
                  className="legend-swatch"
                  style={{ background: `rgb(${e.color.join(',')})` }}
                />
                <span className="legend-label">{e.label}</span>
              </div>
            ))}
        </div>
      )}
    </li>
  )
}

function LayerPanel({
  layers,
  onToggle,
  onZoom,
  onRemove,
  onExport,
  onLoadPreview,
  onMention,
  onUndo,
  onRedo,
  canUndo,
  canRedo,
  onAppearanceChange,
}) {
  const [detailLayerId, setDetailLayerId] = useState(null)
  const detailLayer = layers.find((layer) => layer.layerId === detailLayerId) ?? null

  return (
    <div className="layer-panel">
      <div className="layer-panel-header">
        <h3>レイヤー（{layers.length}）</h3>
        <div className="history-buttons">
          <button className="icon-btn" onClick={onUndo} disabled={!canUndo} title="元に戻す">
            ↶
          </button>
          <button className="icon-btn" onClick={onRedo} disabled={!canRedo} title="やり直す">
            ↷
          </button>
        </div>
      </div>

      {layers.length === 0 ? (
        <p className="empty-state">
          データを取り込むか、プロンプトでレイヤーを生成しましょう。
        </p>
      ) : (
        <ul className="layer-list">
          {layers.map((l) => (
            <LayerRow
              key={l.layerId}
              layer={l}
              onToggle={onToggle}
              onZoom={onZoom}
              onRemove={onRemove}
              onMention={onMention}
              onShowDetail={(layer) => setDetailLayerId(layer.layerId)}
            />
          ))}
        </ul>
      )}

      {detailLayer && (
        <LayerDetailModal
          key={detailLayer.layerId}
          layer={detailLayer}
          onClose={() => setDetailLayerId(null)}
          onExport={onExport}
          onLoadPreview={onLoadPreview}
          onAppearanceChange={onAppearanceChange}
        />
      )}
    </div>
  )
}

export default LayerPanel
